import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import { cn } from '@/lib/utils';
import { columnOrderState } from '@/states/column-order.state';
import { rowEditingAtomFamily } from '@/states/editing-cell.state';
import { hasCouponsInCurrentViewState } from '@/states/has-coupons-in-current-view.state';
import { kpiComparisonState } from '@/states/kpi-comparison.state';
import { recentlyMovedGroupKeysState } from '@/states/recently-moved-group-keys.state';
import {
  getRowSelectionStatus,
  rowSelectionModelState,
} from '@/states/row-selection.state';
import type { Row } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';

import { hasShopifyIdsInCurrentViewState } from '../../states/has-shopify-ids-in-current-view.state';

export type SetupTableRowProps = {
  row: Row<DealItemWithVersions>;
  /**
   * Used only as a memo dependency to ensure the row re-computes when
   * business visibility / user masks hide/show columns.
   */
  columnVisibility?: Record<string, boolean>;
  /** Pré-calculé dans TableProducts (une seule souscription à productsMapState). */
  isRowCategoryUpdating: boolean;
  /** Snapshot TanStack expand ; requis pour que React.memo voie les toggles sans changer row ref. */
  isExpanded: boolean;
};

function SetupTableRowInner({
  row,
  columnVisibility,
  isRowCategoryUpdating,
  isExpanded,
}: SetupTableRowProps) {
  const hasSubRows = row.subRows && row.subRows.length > 1;

  const selectionModel = useAtomValue(rowSelectionModelState);
  const isSelected = getRowSelectionStatus(selectionModel, row) === true;
  const isLeafRow = typeof row.parentId !== 'undefined';
  const { i18n, t } = useTranslation();
  const rowId = hasSubRows
    ? row.id
    : (row.original?.activeVersion?.id ?? row.id);

  const rowEditingField = useAtomValue(rowEditingAtomFamily(rowId));
  const columnOrder = useAtomValue(columnOrderState);
  const kpiComparison = useAtomValue(kpiComparisonState);
  const hasCouponsInCurrentView = useAtomValue(hasCouponsInCurrentViewState);
  const hasShopifyIdsInCurrentView = useAtomValue(
    hasShopifyIdsInCurrentViewState
  );
  const recentlyMovedGroupKeys = useAtomValue(recentlyMovedGroupKeysState);
  const isOfferLocked = useIsOfferLocked();

  const productGroupKey = row.original?.activeVersion?.productGroupKey;
  const isRecentlyMoved =
    !!productGroupKey && recentlyMovedGroupKeys.has(productGroupKey);

  const isSubrow = isLeafRow && (row.getParentRow()?.subRows.length ?? 0 > 1);

  return useMemo(() => {
    const categoryUpdatingTitle = isRowCategoryUpdating
      ? t('general.categorization_in_progress')
      : undefined;

    if (isLeafRow && row.getParentRow()?.subRows.length === 1) {
      return null;
    }

    const visibleCells = row.getVisibleCells();
    const lastLeftPinnedCell = [...visibleCells]
      .reverse()
      .find((c) => c.column.getIsPinned() === 'left');
    const firstNonLeftPinnedCell = visibleCells.find(
      (c) => c.column.getIsPinned() !== 'left'
    );

    return (
      <div
        key={row.id}
        data-table-row
        data-state={isSelected && 'selected'}
        data-recently-moved={isRecentlyMoved || undefined}
        data-category-updating={isRowCategoryUpdating || undefined}
        aria-busy={isRowCategoryUpdating || undefined}
        title={categoryUpdatingTitle}
        className={cn(
          'group flex grow animate-appear flex-col border-b border-b-gray-100 outline-none',
          isRowCategoryUpdating && 'cursor-not-allowed'
        )}
        tabIndex={0}
      >
        {/* Table row */}
        <div
          className={cn(
            'group/row flex h-10 min-w-full',
            isRowCategoryUpdating &&
              'pointer-events-none opacity-[0.88] saturate-75'
          )}
        >
          {visibleCells.map((cell) => (
            <div
              key={cell.id}
              data-table-cell
              className={cn(
                `flex items-center text-sm text-gray-700 group-hover:bg-gray-50`,
                {
                  // Base background colors
                  'bg-white': !hasSubRows && !isSubrow,
                  'bg-gray-50 group-hover:bg-gray-50': hasSubRows,
                  'bg-gray-50 group-hover:bg-gray-100': isSubrow,
                  // Borders: no left border on first non-pinned cell; right border on last left-pinned cell
                  'border-l border-gray-100':
                    !firstNonLeftPinnedCell ||
                    cell.column.id !== firstNonLeftPinnedCell.column.id,
                  'border-r border-gray-100':
                    !!lastLeftPinnedCell &&
                    cell.column.id === lastLeftPinnedCell.column.id,
                  // Pinning
                  sticky: cell.column.getIsPinned(),
                  'z-[1]': cell.column.getIsPinned(),
                  'ml-auto': cell.column.getIsPinned() === 'right',
                }
              )}
              style={{
                left:
                  cell.column.getIsPinned() !== 'right'
                    ? cell.column.getStart()
                    : undefined,
                right: cell.column.getIsPinned() === 'right' ? '0' : undefined,
                width: cell.column.getSize(),
              }}
            >
              {/* Table cell */}
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </div>
          ))}
        </div>
      </div>
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    row,
    rowEditingField,
    i18n.language,
    isSelected,
    isExpanded,
    columnOrder,
    columnVisibility,
    kpiComparison,
    hasCouponsInCurrentView,
    hasShopifyIdsInCurrentView,
    isRecentlyMoved,
    isOfferLocked,
    isRowCategoryUpdating,
  ]);
}

function areSetupTableRowPropsEqual(
  prev: Readonly<SetupTableRowProps>,
  next: Readonly<SetupTableRowProps>
): boolean {
  // For parent rows, re-render if any child's original changed (aggregated values like stock sum)
  if (next.row.subRows.length > 1) {
    const childOrigChanged = next.row.subRows.some(
      (r, i) => r.original !== prev.row.subRows[i]?.original
    );
    if (childOrigChanged) return false;
  }

  return (
    prev.row.original === next.row.original &&
    prev.columnVisibility === next.columnVisibility &&
    prev.isRowCategoryUpdating === next.isRowCategoryUpdating &&
    prev.isExpanded === next.isExpanded
  );
}

export const SetupTableRow = React.memo(
  SetupTableRowInner,
  areSetupTableRowPropsEqual
);
